import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/** Only the topmost open modal answers Escape and Tab. */
const openModals: HTMLElement[] = []

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)]
}

/**
 * Initial focus goes to the child marked `data-autofocus`, else to «Закрыть»; never to an apply control.
 * While `busy`, Escape and the backdrop do not close it.
 */
export function StorefrontModal({
  title,
  labelledBy,
  children,
  onClose,
  footer,
  wide = false,
  busy = false,
  stickyFooter = false,
}: {
  title: string
  labelledBy: string
  children: ReactNode
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  busy?: boolean
  stickyFooter?: boolean
}) {
  const panel = useRef<HTMLElement | null>(null)
  const closeButton = useRef<HTMLButtonElement | null>(null)
  const pressedOnBackdrop = useRef(false)
  const latest = useRef({ onClose, busy })

  useEffect(() => {
    latest.current = { onClose, busy }
  })

  useEffect(() => {
    const root = panel.current
    if (root === null) return undefined
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    openModals.push(root)
    const initial = root.querySelector<HTMLElement>('[data-autofocus]') ?? closeButton.current
    initial?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (openModals.at(-1) !== root) return
      if (event.key === 'Escape') {
        if (event.defaultPrevented || latest.current.busy) return
        event.preventDefault()
        latest.current.onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables(root)
      const first = items[0]
      const last = items.at(-1)
      if (first === undefined || last === undefined) {
        event.preventDefault()
        return
      }
      const active = document.activeElement
      if (active === null || !root.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      openModals.splice(openModals.indexOf(root), 1)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  // A drag that starts inside the panel and ends on the backdrop is not a backdrop click.
  const onBackdropMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    pressedOnBackdrop.current = event.target === event.currentTarget
  }
  const onBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    const fromBackdrop = pressedOnBackdrop.current && event.target === event.currentTarget
    pressedOnBackdrop.current = false
    if (fromBackdrop && !busy) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-onyx/35 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-busy={busy || undefined}
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <section ref={panel} className={`max-h-[90vh] w-full overflow-y-auto rounded-xl border border-rule bg-paper p-5 shadow-2xl ${wide ? 'max-w-3xl' : 'max-w-2xl'}`}>
        <header className="flex items-start justify-between gap-4 border-b border-rule pb-4">
          <h2 id={labelledBy} className="text-xl font-semibold text-ink">{title}</h2>
          <button ref={closeButton} type="button" className="btn btn-ghost shrink-0" onClick={onClose}>Закрыть</button>
        </header>
        <div className="pt-4">{children}</div>
        {footer ? <footer className={`${stickyFooter ? 'sticky bottom-0 z-20 -mx-5 -mb-5 bg-paper px-5 pb-5' : ''} mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-rule pt-4`}>{footer}</footer> : null}
      </section>
    </div>
  )
}
